import { useEffect, useState, useMemo, useRef } from "react";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import { fnbAPI, fmtDate } from "./fnbApi";
import { toast } from "sonner";
import { ChefHat, Wine, Clock, User, ArrowRight, Check, X } from "lucide-react";

const NEXT_STATUS = {
  new: "accepted", accepted: "preparing", preparing: "ready", ready: "served",
};
const STATUS_LABEL = {
  new: "New", accepted: "Accepted", preparing: "Preparing", ready: "Ready", served: "Served",
};
const COLS = ["new", "accepted", "preparing", "ready"];
// Column header tokens (spec): NEW #E6FEFF/#014751 · PREPARING #FEF3C7/#78530E · READY #DBFCEF/#014751 · SERVED #EEF1F4/#3A4A5A
const COL_HDR = {
  new: "bg-hocp-softIce text-hocp-deepTeal", accepted: "bg-hocp-softIce text-hocp-deepTeal",
  preparing: "bg-[#FEF3C7] text-[#78530E]", ready: "bg-hocp-mintSoft text-hocp-deepTeal", served: "bg-[#EEF1F4] text-[#3A4A5A]",
};
const DOT = { new: "bg-[#3A4A5A]", accepted: "bg-hocp-deepTeal", preparing: "bg-[#F59E0B]", ready: "bg-hocp-mintDeep" };

/**
 * KDS-style board.
 * Props:
 *   filterKind: "kitchen" | "bar"
 *   title: string
 *   Icon: LucideIcon
 */
export default function FnbKDS({ filterKind = "kitchen", title = "Kitchen Display", Icon = ChefHat, testidPrefix = "kds" }) {
  const [tickets, setTickets] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const bp = useBreakpoint();
  const [tab, setTab] = useState("new");
  const touchX = useRef(null);
  const swipe = (dir) => setTab((t) => COLS[Math.min(Math.max(COLS.indexOf(t) + dir, 0), COLS.length - 1)]);
  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current; touchX.current = null;
    if (dx < -60) swipe(1); else if (dx > 60) swipe(-1);
  };

  const load = () => Promise.all([
    fnbAPI.tickets({ limit: 200 }), fnbAPI.outlets(),
  ]).then(([t, o]) => { setTickets(t); setOutlets(o); });
  useEffect(() => { load(); const iv = setInterval(load, 5000); return () => clearInterval(iv); }, []);

  const outletIds = useMemo(() =>
    new Set(outlets.filter(o => o.kind === filterKind).map(o => o.id)),
    [outlets, filterKind]);

  const relevantTickets = useMemo(() => tickets.filter(t => outletIds.has(t.production_outlet_id)), [tickets, outletIds]);

  // Flatten to lines with parent ticket info; group by production_status
  const groups = useMemo(() => {
    const g = { new: [], accepted: [], preparing: [], ready: [] };
    for (const t of relevantTickets) {
      for (const L of (t.lines || [])) {
        if (["served", "cancelled"].includes(L.production_status)) continue;
        (g[L.production_status] ||= []).push({ ...L, ticket: t });
      }
    }
    return g;
  }, [relevantTickets]);

  const advance = async (line) => {
    const next = NEXT_STATUS[line.production_status];
    if (!next) return;
    try {
      await fnbAPI.setLineStatus(line.ticket.id, line.line_id, next);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const cancel = async (line) => {
    if (!window.confirm(`Cancel ${line.menu_item_name}?`)) return;
    try { await fnbAPI.setLineStatus(line.ticket.id, line.line_id, "cancelled"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid={`${testidPrefix}-page`}>
      <div className="on-dark bg-gradient-to-br from-[#0F1530] to-[#070B1F] rounded-2xl p-4 md:p-5 border border-white/10 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/10 ring-1 ring-white/15 flex items-center justify-center text-hocp-gold">
            <Icon className="w-5 h-5"/>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-300 font-medium">Production Terminal</div>
            <div className="font-display text-[20px] font-bold text-white">{title}</div>
          </div>
        </div>
        <div className="flex flex-wrap gap-3 md:gap-4 text-[12px] text-slate-200">
          {["new","accepted","preparing","ready"].map(s => (
            <div key={s} className="flex items-center gap-1.5">
              <span className={`w-2.5 h-2.5 rounded-full ${
                s === "new" ? "bg-slate-400" :
                s === "accepted" ? "bg-sky-400" :
                s === "preparing" ? "bg-amber-400" :
                "bg-emerald-400"}`}/>
              {STATUS_LABEL[s]}
              <span className="tabular-nums font-semibold text-white">{groups[s]?.length || 0}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Mobile: tab bar, one column at a time, swipe between */}
      {bp === "mobile" && (
        <div className="flex border-b border-hocp-border -mx-1 overflow-x-auto" role="tablist" data-testid={`${testidPrefix}-tabs`}>
          {COLS.map(col => (
            <button key={col} role="tab" type="button" aria-selected={tab === col} onClick={() => setTab(col)} data-testid={`${testidPrefix}-tab-${col}`}
              className={`flex-1 min-w-[88px] min-h-[44px] px-2 flex items-center justify-center gap-1.5 text-[13px] font-semibold border-b-2 -mb-px transition-colors ${tab === col ? "text-hocp-mintDeep border-hocp-mint" : "text-hocp-textMuted border-transparent"}`}>
              {STATUS_LABEL[col]}
              <span className="text-[11px] tabular-nums px-1.5 py-0.5 rounded-full bg-hocp-mintSoft text-hocp-deepTeal" data-testid={`${testidPrefix}-tab-count-${col}`}>{groups[col]?.length || 0}</span>
            </button>
          ))}
        </div>
      )}

      {/* Desktop: 4-col kanban · Tablet: 2 visible cols + horizontal scroll · Mobile: active tab only */}
      <div onTouchStart={bp === "mobile" ? onTouchStart : undefined} onTouchEnd={bp === "mobile" ? onTouchEnd : undefined}
        className={bp === "mobile" ? "grid grid-cols-1 gap-3" : "flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2 lg:grid lg:grid-cols-4 lg:overflow-visible lg:pb-0"}
        data-testid={`${testidPrefix}-board`} data-layout={bp}>
        {(bp === "mobile" ? [tab] : COLS).map(col => (
          <div key={col} className="bg-hocp-bgSubtle border border-hocp-border rounded-2xl p-3 space-y-2 min-h-[400px] snap-start shrink-0 w-full md:w-[calc(50%-6px)] lg:w-auto lg:shrink lg:max-h-[calc(100vh-260px)] lg:overflow-y-auto" data-testid={`${testidPrefix}-col-${col}`}>
            <div className={`flex items-center justify-between px-3 py-2 -mx-3 -mt-3 mb-2 rounded-t-2xl ${COL_HDR[col]}`}>
              <div className="text-[11px] uppercase tracking-widest font-semibold flex items-center gap-1.5"><span className={`w-2 h-2 rounded-full ${DOT[col]}`} />{STATUS_LABEL[col]}</div>
              <div className="text-[11px] tabular-nums bg-white/70 px-2 py-0.5 rounded-full">{groups[col]?.length || 0}</div>
            </div>
            {(groups[col] || []).length === 0 ? (
              <div className="text-center text-[12px] text-hocp-textMuted py-6">No items</div>
            ) : (groups[col] || []).map(line => (
              <div key={line.line_id} data-testid={`${testidPrefix}-line-${line.line_id}`}
                className="bg-white border border-hocp-border rounded-xl p-3 shadow-sm hover:shadow-md transition-all">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-[14px] font-semibold text-hocp-textPrimary">{line.qty}× {line.menu_item_name}</div>
                    <div className="text-[11px] text-hocp-textMuted font-mono">{line.ticket.order_code}</div>
                  </div>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#EEF1F4] text-[#3A4A5A] uppercase">{line.ticket.service_location_kind?.replace("_"," ")}</span>
                </div>
                {(line.ticket.table_number || line.ticket.room_number) && (
                  <div className="mt-1.5 text-[11px] text-hocp-textMuted flex items-center gap-1">
                    {line.ticket.table_number ? <>Table <b className="text-hocp-deepTeal">{line.ticket.table_number}</b></> : null}
                    {line.ticket.room_number ? <>Room <b className="text-hocp-deepTeal">{line.ticket.room_number}</b></> : null}
                    {line.ticket.guest_name && <span className="ml-1 flex items-center gap-1"><User className="w-3 h-3"/>{line.ticket.guest_name}</span>}
                  </div>
                )}
                {line.special_instructions && (
                  <div className="mt-1.5 text-[11px] text-[#78530E] bg-[#FEF3C7] rounded-md px-2 py-1">Note: {line.special_instructions}</div>
                )}
                <div className="mt-2 flex items-center gap-1.5">
                  <button onClick={() => advance(line)} data-testid={`${testidPrefix}-advance-${line.line_id}`}
                    className="flex-1 min-h-[40px] px-2 py-1.5 rounded-lg bg-hocp-mint text-hocp-deepTeal text-[12px] font-bold hover:bg-hocp-mintStrong inline-flex items-center justify-center gap-1">
                    {col === "ready" ? <><Check className="w-3.5 h-3.5"/>Mark Served</> : <>{STATUS_LABEL[NEXT_STATUS[col]]}<ArrowRight className="w-3.5 h-3.5"/></>}
                  </button>
                  {["new","accepted","preparing"].includes(col) && (
                    <button onClick={() => cancel(line)} data-testid={`${testidPrefix}-cancel-${line.line_id}`}
                      className="min-h-[40px] w-10 rounded-lg bg-[#FEE2E2] text-[#991B1B] hover:bg-[#FECACA] inline-flex items-center justify-center" aria-label="Remove"><X size={14} strokeWidth={2} /></button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
